import React, { useState, useEffect, useRef } from 'react';
import { Trophy, Play, Pause, Crown, Medal, Award, Calendar, Music, X, Video, Mic, Trash2, TriangleAlert as AlertTriangle, User } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { AudioWaveform } from '../components/AudioWaveform';
import { useUser } from '../context/UserContext';

interface LeaderboardEntry {
  rank: number;
  performance_id: string;
  recording_id: string;
  user_id: string;
  username: string;
  full_name: string;
  avatar_url: string;
  song_id: string;
  song_title: string;
  song_artist: string;
  song_cover_url: string;
  overall_score: number;
  pitch_accuracy: number;
  timing_accuracy: number;
  recording_url: string;
  recording_type: string;
  final_video_url: string | null;
  mixed_recording_url: string | null;
  vocals_only_url: string | null;
  backing_track_url: string | null;
  created_at: string;
}

type TimeFilter = 'all' | 'month' | 'week';

export const Leaderboard: React.FC = () => {
  const { profile } = useUser();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [playingEntry, setPlayingEntry] = useState<LeaderboardEntry | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [removingEntry, setRemovingEntry] = useState<string | null>(null);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const backingTrackRef = useRef<HTMLAudioElement>(null);

  const isAdmin = profile?.role === 'admin' || profile?.role === 'technical';

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    loadLeaderboard();
  }, [timeFilter]);

  useEffect(() => {
    const video = videoRef.current;
    const audio = backingTrackRef.current;

    if (!video || !audio || !playingEntry?.backing_track_url) return;

    video.volume = 1.0;
    audio.volume = 0.25;

    const handlePlay = () => {
      setIsVideoPlaying(true);
      audio.currentTime = video.currentTime;
      audio.play().catch(err => console.error('Error playing backing track:', err));
    };

    const handlePause = () => {
      setIsVideoPlaying(false);
      audio.pause();
    };

    const handleSeeked = () => {
      audio.currentTime = video.currentTime;
      if (!video.paused) {
        audio.play().catch(err => console.error('Error playing backing track:', err));
      }
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, [playingEntry]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);

      let dateFilter = '';
      const now = new Date();

      if (timeFilter === 'week') {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        dateFilter = weekAgo.toISOString();
      } else if (timeFilter === 'month') {
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        dateFilter = monthAgo.toISOString();
      }

      let query = supabase
        .from('leaderboard_entries')
        .select('*')
        .order('overall_score', { ascending: false })
        .limit(100);

      if (dateFilter) {
        query = query.gte('created_at', dateFilter);
      }

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      console.log('[Leaderboard] Raw data (first 3):', JSON.stringify(data?.slice(0, 3), null, 2));

      const formattedEntries: LeaderboardEntry[] = (data || []).map((item: any, index: number) => ({
        rank: index + 1,
        performance_id: item.performance_id,
        recording_id: item.recording_id || '',
        user_id: item.user_id || '',
        username: item.username || 'Unknown',
        full_name: item.full_name || 'Anonymous User',
        avatar_url: item.avatar_url || '',
        song_id: item.song_id || '',
        song_title: item.song_title || 'Unknown Song',
        song_artist: item.song_artist || 'Unknown Artist',
        song_cover_url: item.song_cover_url || '',
        overall_score: item.overall_score || 0,
        pitch_accuracy: item.pitch_accuracy || 0,
        timing_accuracy: item.timing_accuracy || 0,
        recording_url: item.recording_url || '',
        recording_type: item.recording_type || 'audio',
        final_video_url: item.final_video_url || null,
        mixed_recording_url: item.mixed_recording_url || null,
        vocals_only_url: item.vocals_only_url || null,
        backing_track_url: item.backing_track_url || null,
        created_at: item.created_at
      }));

      setEntries(formattedEntries);
    } catch (err: any) {
      console.error('Error loading leaderboard:', err);
      setError(err.message || 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (entry: LeaderboardEntry) => {
    console.log('[Leaderboard] handlePlay FULL entry:', JSON.stringify(entry, null, 2));
    console.log('[Leaderboard] hasVideo result:', hasVideo(entry));
    console.log('[Leaderboard] getPlayableUrl result:', getPlayableUrl(entry));
    setPlayingEntry(entry);
  };

  const closePlayer = () => {
    setPlayingEntry(null);
    setIsVideoPlaying(false);
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="text-yellow-400" size={24} />;
      case 2:
        return <Medal className="text-gray-300" size={24} />;
      case 3:
        return <Award className="text-amber-600" size={24} />;
      default:
        return null;
    }
  };

  const getRankStyle = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 border-yellow-500/50';
      case 2:
        return 'bg-gradient-to-r from-gray-400/20 to-gray-500/10 border-gray-400/50';
      case 3:
        return 'bg-gradient-to-r from-amber-600/20 to-amber-700/10 border-amber-600/50';
      default:
        return 'bg-kod-purple-700/50 border-kod-purple-600/30';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const hasVideo = (entry: LeaderboardEntry) => {
    return !!entry.final_video_url;
  };

  const getPlayableUrl = (entry: LeaderboardEntry) => {
    if (entry.final_video_url) {
      return entry.final_video_url;
    }
    return entry.mixed_recording_url || entry.vocals_only_url || entry.recording_url;
  };

  const handleRemoveFromLeaderboard = async (recordingId: string) => {
    if (!isAdmin || !recordingId) return;

    setRemovingEntry(recordingId);
    try {
      const { error: updateError } = await supabase
        .from('recordings')
        .update({ leaderboard_status: 'removed' })
        .eq('id', recordingId);

      if (updateError) throw updateError;

      await loadLeaderboard();
    } catch (err: any) {
      console.error('Error removing entry from leaderboard:', err);
      setError(err.message || 'Failed to remove entry');
    } finally {
      setRemovingEntry(null);
    }
  };

  const handleClearAllLeaderboard = async () => {
    if (!isAdmin) return;

    setClearingAll(true);
    try {
      console.log('[Leaderboard] Starting clear all...');

      const { data, error: updateError, count, status, statusText } = await supabase
        .from('recordings')
        .update({ leaderboard_status: 'removed' })
        .eq('leaderboard_status', 'submitted')
        .select();

      console.log('[Leaderboard] Clear all result:', { data, error: updateError, count, status, statusText });

      if (updateError) throw updateError;

      console.log('[Leaderboard] Updated rows:', data?.length || 0);

      await loadLeaderboard();
      setShowClearAllConfirm(false);
    } catch (err: any) {
      console.error('Error clearing leaderboard:', err);
      setError(err.message || 'Failed to clear leaderboard');
    } finally {
      setClearingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-5">
          <img
            src="/TSL_Logo.png"
            alt="Logo"
            className="w-[600px] h-[600px] object-contain"
          />
        </div>
        <div className="relative z-10 animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-kod-accent"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="text-yellow-400" size={36} />
          <h1 className="text-4xl font-bold">Leaderboard</h1>
        </div>
        <p className="text-white/70 mb-6">Top performances ranked by score</p>

        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setTimeFilter('all')}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
              timeFilter === 'all'
                ? 'bg-kod-accent text-white'
                : 'bg-kod-purple-700 hover:bg-kod-purple-600'
            }`}
          >
            <Calendar size={16} />
            All Time
          </button>
          <button
            onClick={() => setTimeFilter('month')}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
              timeFilter === 'month'
                ? 'bg-kod-accent text-white'
                : 'bg-kod-purple-700 hover:bg-kod-purple-600'
            }`}
          >
            <Calendar size={16} />
            This Month
          </button>
          <button
            onClick={() => setTimeFilter('week')}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
              timeFilter === 'week'
                ? 'bg-kod-accent text-white'
                : 'bg-kod-purple-700 hover:bg-kod-purple-600'
            }`}
          >
            <Calendar size={16} />
            This Week
          </button>
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => setShowOnlyMine(!showOnlyMine)}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                showOnlyMine
                  ? 'bg-kod-accent text-white'
                  : 'bg-kod-purple-700 hover:bg-kod-purple-600'
              }`}
            >
              <Trophy size={16} />
              My Performances
            </button>
            {isAdmin && (
              <button
                onClick={() => setShowClearAllConfirm(true)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
                disabled={clearingAll}
              >
                <Trash2 size={16} />
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 text-red-400">
          {error}
        </div>
      )}

      {entries.filter(e => !showOnlyMine || e.user_id === currentUserId).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {entries.filter(e => !showOnlyMine || e.user_id === currentUserId).slice(0, 3).map((entry) => (
            <div
              key={entry.performance_id}
              className={`relative rounded-xl p-6 border-2 transition-all duration-300 hover:scale-[1.02] ${getRankStyle(entry.rank)}`}
            >
              <div className="absolute top-4 right-4">
                {getRankIcon(entry.rank)}
              </div>

              <div className="flex flex-col items-center text-center">
                <div className="relative mb-4">
                  <div className={`absolute -inset-1 rounded-full ${
                    entry.rank === 1 ? 'bg-yellow-400/30' :
                    entry.rank === 2 ? 'bg-gray-300/30' :
                    'bg-amber-600/30'
                  } blur`} />
                  {entry.avatar_url ? (
                    <img
                      src={entry.avatar_url}
                      alt={entry.full_name}
                      className="relative w-20 h-20 rounded-full object-cover border-4 border-white/20"
                    />
                  ) : (
                    <div className="relative w-20 h-20 rounded-full bg-blue-600 border-4 border-white/20 flex items-center justify-center">
                      <User size={36} className="text-white" />
                    </div>
                  )}
                  <div className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    entry.rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                    entry.rank === 2 ? 'bg-gray-300 text-gray-800' :
                    'bg-amber-600 text-white'
                  }`}>
                    #{entry.rank}
                  </div>
                </div>

                <h3 className="font-bold text-lg mb-1">{entry.full_name || entry.username}</h3>
                <p className="text-white/60 text-sm mb-3">@{entry.username}</p>

                <div className="flex items-center gap-2 mb-3">
                  <img
                    src={entry.song_cover_url || 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=40&h=40&fit=crop'}
                    alt={entry.song_title}
                    className="w-10 h-10 rounded object-cover"
                  />
                  <div className="text-left">
                    <p className="text-sm font-medium truncate max-w-[150px]">{entry.song_title}</p>
                    <p className="text-xs text-white/60 truncate max-w-[150px]">{entry.song_artist}</p>
                  </div>
                </div>

                <div className={`text-3xl font-bold mb-3 ${
                  entry.rank === 1 ? 'text-yellow-400' :
                  entry.rank === 2 ? 'text-gray-300' :
                  'text-amber-500'
                }`}>
                  {entry.overall_score.toFixed(1)}
                </div>

                <button
                  onClick={() => handlePlay(entry)}
                  className={`w-full py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    entry.rank === 1 ? 'bg-yellow-400/20 hover:bg-yellow-400/30 text-yellow-400' :
                    entry.rank === 2 ? 'bg-gray-300/20 hover:bg-gray-300/30 text-gray-300' :
                    'bg-amber-600/20 hover:bg-amber-600/30 text-amber-500'
                  }`}
                >
                  <Play size={16} />
                  Play Recording
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <h2 className="text-xl font-bold mb-4">{showOnlyMine ? 'My Rankings' : 'All Rankings'}</h2>

        {entries.filter(e => !showOnlyMine || e.user_id === currentUserId).length === 0 ? (
          <div className="text-center py-12 text-white/50">
            <Trophy size={64} className="mx-auto mb-4 opacity-50" />
            <p className="text-xl">{showOnlyMine ? 'No performances found' : 'No performances yet'}</p>
            <p className="text-sm mt-2">{showOnlyMine ? 'Record and submit a song to appear on the leaderboard!' : 'Be the first to make it to the leaderboard!'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {entries.filter(e => !showOnlyMine || e.user_id === currentUserId).map((entry) => (
              <div
                key={entry.performance_id}
                className={`flex items-center gap-4 p-4 rounded-lg border transition-all hover:bg-kod-purple-700/30 ${getRankStyle(entry.rank)}`}
              >
                <div className="flex items-center justify-center w-12">
                  {getRankIcon(entry.rank) || (
                    <span className="text-lg font-bold text-white/60">#{entry.rank}</span>
                  )}
                </div>

                {entry.avatar_url ? (
                  <img
                    src={entry.avatar_url}
                    alt={entry.full_name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-white/20"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-blue-600 border-2 border-white/20 flex items-center justify-center">
                    <User size={22} className="text-white" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">{entry.full_name || entry.username}</h3>
                    {hasVideo(entry) ? (
                      <Video size={14} className="text-kod-accent flex-shrink-0" />
                    ) : (
                      <Mic size={14} className="text-kod-accent flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/60">
                    <Music size={12} />
                    <span className="truncate">{entry.song_title} - {entry.song_artist}</span>
                  </div>
                </div>

                <div className="hidden sm:flex items-center gap-4 text-sm text-white/60">
                  <div className="text-center">
                    <div className="text-xs text-white/40">Pitch</div>
                    <div className="font-medium text-white/80">{entry.pitch_accuracy.toFixed(1)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-white/40">Timing</div>
                    <div className="font-medium text-white/80">{entry.timing_accuracy.toFixed(1)}</div>
                  </div>
                </div>

                <div className="text-right">
                  <div className={`text-2xl font-bold ${
                    entry.rank === 1 ? 'text-yellow-400' :
                    entry.rank === 2 ? 'text-gray-300' :
                    entry.rank === 3 ? 'text-amber-500' :
                    'text-white'
                  }`}>
                    {entry.overall_score.toFixed(1)}
                  </div>
                  <div className="text-xs text-white/40">{formatDate(entry.created_at)}</div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handlePlay(entry)}
                    className="p-3 bg-kod-purple-600 hover:bg-kod-purple-500 rounded-full transition-colors"
                    title="Play recording"
                  >
                    <Play size={18} />
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => handleRemoveFromLeaderboard(entry.recording_id)}
                      disabled={removingEntry === entry.recording_id}
                      className="p-3 bg-red-600/80 hover:bg-red-600 rounded-full transition-colors disabled:opacity-50"
                      title="Remove from leaderboard"
                    >
                      <Trash2 size={18} className={removingEntry === entry.recording_id ? 'animate-pulse' : ''} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {playingEntry && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-kod-purple-800 rounded-lg overflow-hidden max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-kod-purple-600">
              <div className="flex items-center gap-4">
                {playingEntry.avatar_url ? (
                  <img
                    src={playingEntry.avatar_url}
                    alt={playingEntry.full_name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
                    <User size={22} className="text-white" />
                  </div>
                )}
                <div>
                  <h3 className="font-bold">{playingEntry.full_name || playingEntry.username}</h3>
                  <p className="text-sm text-white/70">
                    {playingEntry.song_title} - {playingEntry.song_artist}
                  </p>
                </div>
                <div className="ml-4 px-3 py-1 bg-kod-accent/20 rounded-full">
                  <span className="font-bold text-kod-accent">{playingEntry.overall_score.toFixed(1)}</span>
                </div>
              </div>
              <button
                onClick={closePlayer}
                className="p-2 hover:bg-kod-purple-700 rounded-full transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-auto">
              {hasVideo(playingEntry) ? (
                <div className="aspect-video bg-black relative">
                  <video
                    ref={videoRef}
                    src={getPlayableUrl(playingEntry) || ''}
                    className="w-full h-full"
                    controls
                    playsInline
                    preload="auto"
                  />
                  {playingEntry.backing_track_url && (
                    <audio
                      ref={backingTrackRef}
                      src={playingEntry.backing_track_url}
                      preload="auto"
                      style={{ display: 'none' }}
                    />
                  )}
                </div>
              ) : (
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <img
                      src={playingEntry.song_cover_url || 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=120&h=120&fit=crop'}
                      alt={playingEntry.song_title}
                      className="w-24 h-24 rounded-lg object-cover"
                    />
                    <div>
                      <h4 className="text-xl font-bold">{playingEntry.song_title}</h4>
                      <p className="text-white/70">{playingEntry.song_artist}</p>
                      <p className="text-sm text-white/50 mt-2">
                        Performed by {playingEntry.full_name || playingEntry.username}
                      </p>
                    </div>
                  </div>
                  <AudioWaveform url={getPlayableUrl(playingEntry) || ''} />
                </div>
              )}
            </div>

            <div className="p-4 border-t border-kod-purple-600 bg-kod-purple-800/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-xs text-white/40 uppercase">Overall</div>
                    <div className="text-xl font-bold text-kod-accent">{playingEntry.overall_score.toFixed(1)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-white/40 uppercase">Pitch</div>
                    <div className="text-lg font-semibold">{playingEntry.pitch_accuracy.toFixed(1)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-white/40 uppercase">Timing</div>
                    <div className="text-lg font-semibold">{playingEntry.timing_accuracy.toFixed(1)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Trophy size={16} className="text-yellow-400" />
                  <span className="font-bold">Rank #{playingEntry.rank}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showClearAllConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-kod-purple-800 rounded-lg p-6 max-w-md w-full border border-red-500/30">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="text-red-500" size={32} />
              <h3 className="text-xl font-bold">Clear All Leaderboard Entries?</h3>
            </div>
            <p className="text-white/70 mb-6">
              This will remove all recordings from the leaderboard. Users will not be able to resubmit removed recordings. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearAllConfirm(false)}
                disabled={clearingAll}
                className="flex-1 px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleClearAllLeaderboard}
                disabled={clearingAll}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {clearingAll ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Clear All
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
